/**
 * ContentItemPreview Component
 * Renders a preview of content item based on its type
 */

import { cn } from '@/lib/utils'
import {
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
  RefreshCw,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type {
  ContentItem,
  ContentItemDetail,
  ContentItemType,
  TextElementContent,
  DataPointContent,
  ImageElementContent,
  TableElementContent,
  EmailSnippetContent,
  SlideElementContent,
} from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface ContentItemPreviewProps {
  item: ContentItem | ContentItemDetail
  className?: string
  showFullContent?: boolean
  onRefreshData?: () => void
}

// ============================================
// Type Icons and Colors
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

// ============================================
// Preview Components
// ============================================

interface TextPreviewProps {
  content: TextElementContent
  showFull?: boolean
}

function TextPreview({ content, showFull }: TextPreviewProps) {
  const displayContent = showFull
    ? content.content
    : content.content.length > 500
      ? content.content.slice(0, 500) + '...'
      : content.content

  return (
    <div className="space-y-3">
      <div className="prose dark:prose-invert max-w-none">
        {content.format === 'Html' ? (
          <div dangerouslySetInnerHTML={{ __html: displayContent }} />
        ) : content.format === 'Markdown' ? (
          <div className="whitespace-pre-wrap font-mono text-sm bg-neutral-50 dark:bg-neutral-800/50 p-4 rounded-lg">
            {displayContent}
          </div>
        ) : (
          <p className="whitespace-pre-wrap">{displayContent}</p>
        )}
      </div>
      <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400 pt-2 border-t border-neutral-100 dark:border-neutral-800">
        <span>{content.wordCount} words</span>
        <span>{content.characterCount} characters</span>
        {content.language && <span>Language: {content.language}</span>}
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {content.format}
        </span>
      </div>
    </div>
  )
}

interface DataPointPreviewProps {
  content: DataPointContent
  onRefresh?: () => void
}

function DataPointPreview({ content, onRefresh }: DataPointPreviewProps) {
  const formatValue = (value: string) => {
    switch (content.valueType) {
      case 'Currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: content.currencyCode || 'USD',
          minimumFractionDigits: content.decimalPlaces ?? 2,
          maximumFractionDigits: content.decimalPlaces ?? 2,
        }).format(parseFloat(value))
      case 'Percentage':
        return `${parseFloat(value).toFixed(content.decimalPlaces ?? 1)}%`
      case 'Number':
        return parseFloat(value).toLocaleString('en-US', {
          minimumFractionDigits: content.decimalPlaces ?? 0,
          maximumFractionDigits: content.decimalPlaces ?? 0,
        })
      case 'Date':
        return new Date(value).toLocaleDateString()
      default:
        return value
    }
  }

  const formattedValue = formatValue(content.currentValue)
  const formattedPrevious = content.previousValue ? formatValue(content.previousValue) : null

  // Calculate trend
  let trend: number | null = null
  if (content.previousValue && content.valueType !== 'Text' && content.valueType !== 'Date') {
    const current = parseFloat(content.currentValue)
    const previous = parseFloat(content.previousValue)
    if (!isNaN(current) && !isNaN(previous) && previous !== 0) {
      trend = ((current - previous) / previous) * 100
    }
  }

  return (
    <div className="text-center py-8">
      <p className="text-4xl font-bold text-neutral-900 dark:text-white">{formattedValue}</p>
      {content.unit && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{content.unit}</p>
      )}

      {trend !== null && (
        <div
          className={cn(
            'inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full text-sm font-medium',
            trend >= 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
          )}
        >
          {trend >= 0 ? '+' : ''}
          {trend.toFixed(1)}%
          {formattedPrevious && (
            <span className="text-xs opacity-75">from {formattedPrevious}</span>
          )}
        </div>
      )}

      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-neutral-500 dark:text-neutral-400">
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {content.valueType}
        </span>
        {content.isLive && (
          <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            Live
          </span>
        )}
        {content.lastRefreshedAt && (
          <span>Updated {new Date(content.lastRefreshedAt).toLocaleString()}</span>
        )}
      </div>

      {content.isLive && onRefresh && (
        <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4 rounded-full gap-2">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      )}
    </div>
  )
}

interface ImagePreviewProps {
  content: ImageElementContent
}

function ImagePreview({ content }: ImagePreviewProps) {
  return (
    <div className="space-y-4">
      <div className="relative rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <img
          src={content.assetUrl}
          alt={content.altText || 'Content image'}
          className="w-full h-auto object-contain max-h-[500px] mx-auto"
          loading="lazy"
        />
      </div>

      {content.caption && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center italic">
          {content.caption}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
        {content.width && content.height && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {content.width} x {content.height}
          </span>
        )}
        {content.aspectRatio && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {content.aspectRatio}
          </span>
        )}
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {(content.fileSize / 1024).toFixed(1)} KB
        </span>
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {content.mimeType}
        </span>
      </div>
    </div>
  )
}

interface TablePreviewProps {
  content: TableElementContent
}

function TablePreview({ content }: TablePreviewProps) {
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
        <table className="w-full">
          {content.hasHeaderRow && (
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                {content.headers.map((header) => (
                  <th
                    key={header.key}
                    className={cn(
                      'px-4 py-3 text-left text-sm font-medium text-neutral-900 dark:text-white border-b border-neutral-200 dark:border-neutral-700',
                      header.alignment === 'Center' && 'text-center',
                      header.alignment === 'Right' && 'text-right'
                    )}
                    style={{ width: header.width ? `${header.width}px` : undefined }}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {content.rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={cn(
                  rowIndex % 2 === 1 && content.style?.alternateRowColor
                    ? 'bg-neutral-50 dark:bg-neutral-800/50'
                    : 'bg-white dark:bg-neutral-900'
                )}
              >
                {content.headers.map((header) => {
                  const cell = row.cells[header.key]
                  return (
                    <td
                      key={header.key}
                      className={cn(
                        'px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300',
                        header.alignment === 'Center' && 'text-center',
                        header.alignment === 'Right' && 'text-right'
                      )}
                    >
                      {cell?.displayValue || cell?.value || ''}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
        <span>{content.rowCount} rows</span>
        <span>{content.columnCount} columns</span>
      </div>
    </div>
  )
}

interface EmailSnippetPreviewProps {
  content: EmailSnippetContent
}

function EmailSnippetPreview({ content }: EmailSnippetPreviewProps) {
  return (
    <div className="space-y-4">
      {content.subject && (
        <div className="pb-3 border-b border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
            Subject
          </p>
          <p className="font-medium text-neutral-900 dark:text-white">{content.subject}</p>
        </div>
      )}

      <div className="prose dark:prose-invert max-w-none">
        {content.bodyFormat === 'Html' ? (
          <div
            className="bg-white dark:bg-neutral-900 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700"
            dangerouslySetInnerHTML={{ __html: content.body }}
          />
        ) : (
          <p className="whitespace-pre-wrap">{content.body}</p>
        )}
      </div>

      {content.signature && (
        <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
            Signature
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
            {content.signature}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400 pt-3 border-t border-neutral-100 dark:border-neutral-800">
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 capitalize">
          {content.category}
        </span>
        {content.tone && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 capitalize">
            {content.tone}
          </span>
        )}
        {content.language && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {content.language}
          </span>
        )}
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {content.bodyFormat}
        </span>
      </div>
    </div>
  )
}

interface SlidePreviewProps {
  content: SlideElementContent
}

function SlidePreview({ content }: SlidePreviewProps) {
  return (
    <div className="space-y-4">
      <div className="aspect-video relative rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
        {content.thumbnailUrl ? (
          <img
            src={content.thumbnailUrl}
            alt="Slide preview"
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : content.assetUrl ? (
          <img
            src={content.assetUrl}
            alt="Slide preview"
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="text-center">
            <Presentation className="h-16 w-16 text-neutral-400 mx-auto mb-2" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No preview available</p>
          </div>
        )}
      </div>

      {content.notes && (
        <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
            Speaker Notes
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
            {content.notes}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 capitalize">
          {content.elementType}
        </span>
        {content.width && content.height && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {content.width} x {content.height}
          </span>
        )}
        {content.slideCount && content.slideCount > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
            {content.slideCount} slide{content.slideCount > 1 ? 's' : ''}
          </span>
        )}
        <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
          {(content.fileSize / 1024).toFixed(1)} KB
        </span>
        {content.originalFileName && (
          <span className="truncate max-w-[200px]">{content.originalFileName}</span>
        )}
      </div>

      <Button variant="outline" size="sm" asChild className="rounded-full gap-2">
        <a href={content.assetUrl} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-3.5 w-3.5" />
          Open Original
        </a>
      </Button>
    </div>
  )
}

// ============================================
// Empty Preview Placeholder
// ============================================

interface EmptyPreviewProps {
  type: ContentItemType
}

function EmptyPreview({ type }: EmptyPreviewProps) {
  const Icon = TYPE_ICONS[type] || FileText
  const color = TYPE_COLORS[type] || 'from-gray-400 to-gray-600'

  return (
    <div className="text-center py-12">
      <div className={cn('inline-flex p-4 rounded-2xl bg-gradient-to-br mb-4', color)}>
        <Icon className="h-8 w-8 text-white" />
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">No content available</p>
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function ContentItemPreview({
  item,
  className,
  showFullContent = false,
  onRefreshData,
}: ContentItemPreviewProps) {
  const renderPreview = () => {
    switch (item.type) {
      case 'Text':
        if (item.textContent) {
          return <TextPreview content={item.textContent} showFull={showFullContent} />
        }
        break
      case 'DataPoint':
        if (item.dataPointContent) {
          return <DataPointPreview content={item.dataPointContent} onRefresh={onRefreshData} />
        }
        break
      case 'Image':
        if (item.imageContent) {
          return <ImagePreview content={item.imageContent} />
        }
        break
      case 'Table':
        if (item.tableContent) {
          return <TablePreview content={item.tableContent} />
        }
        break
      case 'EmailSnippet':
        if (item.emailSnippetContent) {
          return <EmailSnippetPreview content={item.emailSnippetContent} />
        }
        break
      case 'SlideElement':
        if (item.slideContent) {
          return <SlidePreview content={item.slideContent} />
        }
        break
    }

    return <EmptyPreview type={item.type} />
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-6',
        className
      )}
    >
      {renderPreview()}
    </div>
  )
}

export default ContentItemPreview
